// ════════════════════════════════════════════════════════════════
// FloorCost — Configuration Constants
// ════════════════════════════════════════════════════════════════

export const APP_VERSION   = '3.0';
export const GST_M         = 1.1;
export const ROLL_W        = 3.66;
export const COMM_RATE     = 0.03;

// IANA timezone the store operates in. Entry dates are stamped as the
// *local* calendar day in this zone, not UTC — a quote posted at 8am
// Brisbane must not land on the previous day. QLD has no DST, so this is
// a fixed +10 offset today; kept as a named zone so a future non-QLD
// store only changes this constant (or, later, per-store config).
export const STORE_TIMEZONE = 'Australia/Brisbane';
// Per-store config (salespersons, qantas levy, levy bands, suburbs,
// default state) lives in `src/lib/stores.js`. Resolve via
// `getCurrentStoreConfig()` from `src/store-context.js`.

// Margin colour thresholds
export const GP_GOOD = 30;
export const GP_OK   = 27;
export const GP_WARN = 25;
// Quote-health gauge scale (CT-5) — the working GP% window the
// segmented strip under the invoice total spans; margins outside it
// clamp to the ends.
export const GP_GAUGE_MIN = 20;
export const GP_GAUGE_MAX = 40;

// QJC
export const QJC_MARGINS    = [25, 26, 27, 28, 29, 30, 32.5, 35, 40];
export const QJC_UPLIFT_RATE = 14;
export const QJC_STAIRS_RATES = {
  carpet: { std: 15, winder: 20 },
  hf:     { std: 100, winder: 125 },
};

// Blinds — supplier-portal total is entered as a single inc-GST cost
// figure and one of these margin buttons backsolves the customer total.
// Same set as QJC plus a 20 for low-margin promotional pricing.
export const BLINDS_MARGINS = [20, 25, 26, 27, 28, 29, 30, 32.5, 35, 40];

// B-train B3 — the check-measure variance the operator is willing to absorb
// silently, as a % of the quoted supplier cost. Above this (or once actual GP
// drops under GP_OK, whichever fires first) posting raises a warning offering
// a reprice. Warning tier only: absorbing a big variance is sometimes the
// right commercial call, so it is never blocked.
export const BLINDS_VARIANCE_WARN_PCT = 5;

// B-train B4 — the deposit taken at acceptance, as a % of the customer total.
export const BLINDS_DEPOSIT_PCT = 50;

// Minimum labour rule — carpet mode only, not supply-only.
export const QJC_MIN_LABOUR_LM   = 6.00;
export const QJC_MIN_LABOUR_COST = 250.00;

// CH-4 — spreadsheet value-stepping (Ctrl/Cmd+Arrow on a focused grid
// cell). Base step per column; +Shift multiplies coarse. An Alt/fine
// (×0.1) tier is intentionally NOT bound: Ctrl+Alt+Arrow is an OS/GPU
// screen-rotate hotkey on the showroom desktop (the browser never sees
// it), so sub-base cost nudges stay typed. Qty ladder: base 1 / Shift
// 10. Cost ladder: base $0.10 / Shift $1.00. Results land on 2dp — see
// src/lib/grid-step.js.
export const GRID_STEP_QTY    = 1;
export const GRID_STEP_COST   = 0.1;
export const GRID_STEP_COARSE = 10;

// Storage keys
export const STORAGE = {
  form:      'fc_form_v3',
  tracker:   'fc_tracker',
  archive:   'fc_job_archive',
  templates: 'fc_templates',
  theme:     'fc_theme',
  roundUp:   'fc_roundup',
  // PX-4.7 — active-rows-only "job view" vs the full price-book sheet.
  jobView:   'fc_job_view',
  qjc:       'fc_qjc_state',
  blinds:    'fc_blinds_state',
  toolkit:   'fc_toolkit_state',
  toolkitCommunications: 'fc_toolkit_communications_v1',
  toolkitDescription: 'fc_toolkit_description_v1',
  toolkitScopePresets: 'fc_toolkit_scope_presets_v1',
  measure:   'fc_measure_v1',
  auth:      'fc_auth_session',
  // SEC-A2 — "this browser initiated a sign-in" marker (a timestamp).
  // Written when a magic link is requested, required + cleared when a
  // callback hash is consumed. localStorage (not sessionStorage) because
  // the emailed link opens in a fresh tab, and sessionStorage is per-tab.
  authPending: 'fc_auth_pending_v1',
  // C3 PR-1b — passkey-wrapped refresh token. The credential ID +
  // PRF salt + AES-GCM ciphertext of the Supabase refresh_token
  // live under this single key. PRF-derived encryption means the
  // ciphertext is useless without the authenticator that minted it.
  passkey:   'fc_passkey_v1',
  // AUTH-2 — one-shot sign-out result, surfaced on the sign-in screen after
  // sign-out's hard navigation (which wipes in-memory state, so the existing
  // `_callbackError` stash can't carry it). Device-level and read when no uid
  // is available, so it is deliberately NOT namespaced — see ns-storage.js.
  signOutNotice: 'fc_signout_notice_v1',
  syncQueue:     'fc_sync_q',
  syncQueueDead: 'fc_sync_dead',
  decisionLog:   'fc_decision_log',
  // Phase E PR 3 — user-saved tracker filter views. Single-store
  // pilot; per-store namespacing arrives with C3 / Tier B.
  trackerViews:  'fc_tracker_views_v1',
  // Phase E deferred — column sort axis ({by, dir}). Interaction
  // state rather than view state, so kept on its own key.
  trackerSort:   'fc_tracker_sort_v1',
  // Active full-costing edit-session bindings (pfx → entry meta). Lets a
  // reload mid-edit re-bind the banner instead of letting the next post
  // create a duplicate. Cleared per-tab on save/cancel/clear.
  editBindings:  'fc_edit_bindings_v1',
};

// Sync queue — give up after this many consecutive failures and move the op
// to the dead-letter queue for operator inspection.
export const SYNC_MAX_RETRIES = 5;

// Cloud read page size (T2-1 #4). sbAll() KEYSET-pages the sales_entries read
// (id.desc, id < cursor) so a store past a single page's worth of entries still
// fully reaches a fresh device — the old flat `limit=5000` silently truncated
// older rows. Purely a round-trip/latency knob: keyset paging terminates on an
// empty page, so correctness does NOT depend on this staying ≤ the server's
// `db-max-rows` (a capped-short page still advances the cursor).
export const SB_PAGE_SIZE = 1000;
// Safety cap on total pages, so a misbehaving endpoint (e.g. one that keeps
// returning a full page forever) can't spin sbAll() indefinitely. 1000 pages ×
// SB_PAGE_SIZE = 1,000,000 rows — orders of magnitude beyond any real store.
export const SB_MAX_PAGES = 1000;

// Toast
export const TOAST_DURATION_MS = 2500;

// Archive
export const ARCHIVE_MAX_ENTRIES = 500;

// Decision log — pricing decisions captured at post-to-tracker time
export const DECISION_LOG_MAX_ENTRIES = 500;

// Save debounces — each module has its own timer; values are independent.
export const STATE_SAVE_DEBOUNCE_MS = 800;
export const QJC_SAVE_DEBOUNCE_MS   = 600;
export const TOOLKIT_SAVE_DEBOUNCE_MS = 600;

// Tool Kit sub-tab strip — the seven section labels overflow the 880px
// reading column, so the strip scrolls and reveals itself on hover
// ("smart scroll"). Zone depth is roughly one clipped tab's worth, and
// the max speed crosses the whole strip in about two seconds at the
// very edge — fast enough to feel intentional, slow enough to read the
// labels going past. See src/lib/edge-scroll.js for the ramp.
export const SUBNAV_EDGE_ZONE_PX     = 64;
export const SUBNAV_MAX_SPEED_PX_S   = 620;
// Chevron click distance, as a fraction of the visible strip — leaves a
// sliver of the previous tab on screen so the jump stays orientable.
export const SUBNAV_PAGE_RATIO       = 0.8;
// Breathing room left beside a tab scrolled into view on activation.
export const SUBNAV_REVEAL_MARGIN_PX = 24;

// App initialisation sequencing — defers until after the synchronous setup
// tasks on the main thread complete; chosen to avoid race conditions at load.
export const INIT_FORM_DELAY_MS     = 100;
export const INIT_QJC_DELAY_MS      = 150;
export const INIT_CLEAR_DELAY_MS    = 200;
// Edit-binding restore runs after every tab's form has been rehydrated
// (form 100ms, qjc/blinds 150ms), so the banners exist + forms are
// populated before we re-bind an interrupted edit session.
export const INIT_EDIT_RESTORE_DELAY_MS = 220;

// Print
export const PRINT_STYLE_CLEANUP_MS = 500;
export const PRINT_TRIGGER_DELAY_MS = 100;

// UI timing
export const ROOM_SCROLL_DELAY_MS  = 50;
export const PROJ_SCROLL_RETRY_MS  = 200;

// Supabase credentials — injected at build time from environment variables.
// Local dev:  set in `.env` (gitignored — see .env.example)
// Netlify:    set in Site settings → Environment variables
// The publishable key is rotatable and only grants what RLS allows.
export const SB_URL     = import.meta.env.SB_URL;
export const SB_KEY     = import.meta.env.SB_KEY;

// Maintenance mode — set MAINTENANCE_MODE=true in Netlify env vars and
// redeploy to enter. When active, SB_ENABLED is forced false so every
// existing `if (!SB_ENABLED) return;` guard short-circuits — no Supabase
// reads, no writes, no sync flush. src/maintenance.js renders a full-
// screen splash on top of the app. Used during the REGION-1 cutover
// window (see docs/REGION_MIGRATION.md) and reusable for any future
// hard-downtime migration. Pre-flight: drain the sync queue before
// flipping this on, otherwise queued writes resume against whatever
// SB_URL points at when MAINTENANCE_MODE flips off.
export const MAINTENANCE_MODE    = import.meta.env.MAINTENANCE_MODE === 'true';
export const MAINTENANCE_BACK_AT = import.meta.env.MAINTENANCE_BACK_AT || '';

export const SB_ENABLED = !MAINTENANCE_MODE && !!(SB_URL && SB_KEY);
